import React from "react";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import StatCard from "../../../components/shared/StatCard";
import DataTable from "../../../components/shared/DataTable";
import { formatDate, statusBadgeClass } from "../../../utils/helpers";
import api from "../../../utils/api";

export default function PatientDashboard() {
  const { user } = useAuth();

  const { data } = useQuery("patient-dashboard", () =>
    api.get("/patient/dashboard")
  );

  const upcomingColumns = [
    { key: "doctorOrLabName", label: "With" },
    { key: "type", label: "Type" },
    {
      key: "scheduledAt",
      label: "Date & Time",
      render: (row) => formatDate(row.scheduledAt, "dd MMM yyyy, hh:mm a"),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span className={statusBadgeClass(row.status)}>{row.status}</span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-bold text-2xl text-slate-900">
          Welcome back, {user?.name} 👋
        </h2>
        <p className="text-slate-500 text-sm mt-1">
          Here's your health summary
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard
          title="Upcoming Appointments"
          value={data?.upcomingAppointments ?? 2}
          icon="📅"
          color="primary"
        />
        <StatCard
          title="Completed Bookings"
          value={data?.completedBookings ?? 15}
          icon="✅"
          color="success"
        />
        <StatCard
          title="Total Spent"
          value={data?.totalSpent ?? "₹3,200"}
          icon="💳"
          color="teal"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Book Doctor", path: "/patient/search-doctors", icon: "👨‍⚕️", color: "bg-blue-50" },
          { label: "Book Lab Test", path: "/patient/search-diagnostics", icon: "🧪", color: "bg-purple-50" },
          { label: "View History", path: "/patient/booking-history", icon: "📋", color: "bg-green-50" },
          { label: "Raise Issue", path: "/patient/issues", icon: "🆘", color: "bg-orange-50" },
        ].map((action) => (
          <Link
            key={action.label}
            to={action.path}
            className={`${action.color} rounded-xl p-4 text-center hover:shadow-sm transition-shadow`}
          >
            <div className="text-3xl mb-2">{action.icon}</div>
            <p className="text-sm font-medium text-slate-700">{action.label}</p>
          </Link>
        ))}
      </div>

      {/* Upcoming Bookings */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold text-slate-800">
            Upcoming Bookings
          </h3>
          <Link
            to="/patient/booking-history"
            className="text-primary-600 text-sm hover:underline"
          >
            View all →
          </Link>
        </div>
        <DataTable
          columns={upcomingColumns}
          data={data?.upcoming || []}
          emptyMessage="No upcoming bookings."
        />
      </div>
    </div>
  );
}
